/**
 * Threshold segmentation of a scalar volume: the voxels at or above a
 * lower bound — optionally banded, optionally restricted to a region —
 * carried through a fixed cleanup pipeline to a metric signed-distance
 * texture (SR-WOR-H-27-2 for the CT presets, SR-WOR-H-27-3 for SUV; the
 * Body preset's clinical specifics are SR-WOR-H-27-1).
 *
 * Fixed stage order: threshold → [2D cavity fill] → GPU reinitialization
 * to a metric SDF → [Gaussian smoothing] → [min in-plane area] →
 * [keep N largest per slice] → [keep N largest 3D] → [final 2D cavity
 * re-fill] → [symmetric margin] → [keep inside EXTERNAL] →
 * restriction-window clamp. Only the threshold and cavity fill run on the
 * hard binary mask (the CPU phase, cacheable across every later
 * parameter change); the cleanup stages run after smoothing because
 * smoothing can merge, split or shrink marginal components — only a
 * selection made after it guarantees the delivered outline has exactly
 * the requested component structure, and only an area floor applied
 * after it judges the components the user actually sees. The final
 * re-fill exists because smoothing and cleanup can reopen enclosed
 * cavities the mask-phase fill closed (the Body preset locks it on; Bone
 * must NOT re-fill — the spinal canal would be swallowed). The CPU mask
 * phase fills through the per-slice border flood fill, which visits every
 * voxel and therefore needs no metric falloff — hard 0/255 masks would
 * defeat the labeler's SDF skip optimization (see
 * {@link ConnectedComponentOptions}); the post-smoothing cleanup passes
 * label the metric GPU field, where that skip is valid.
 *
 * Thresholds are in the volume's OWN units, which this module does not
 * name: the CT presets feed it HU, the SUV tool raw PET activity. That
 * the volume is quantified in a unit a threshold can be stated in is a
 * domain property the caller establishes ({@link Image3D.isHuQuantified},
 * {@link Image3D.isSuvQuantified}) and re-checks with a typed error
 * before any voxel is read; this module does not re-validate.
 */
import { mat4, vec3 } from 'gl-matrix';
import {
    VoiShape,
    VoxelBounds,
    VoxelSubRange,
    WorldBoundsMm,
    gridSubRange,
    gridVoxelBounds,
    regionCenterXSpan,
    subRangeIsEmpty,
    subRangeIsFullGrid,
} from '~/domain/services/imageService/gridGeometry';
import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import { Grid } from '~/engine/data/common/Grid';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { sampleIntensityMaskSlice } from '~/engine/tool/actions/contouring-actions/constraintClipping';
import { applyGaussianSmoothingSigmaGL } from './GaussianSmoothingGL';
import { BooleanOperation, SDFOperations } from './SDFOperations';
import {
    ConnectedComponentOptions,
    cutFieldToRegion,
    fillCavities2D,
    keepLargestComponents,
    labelConnectedComponents,
    removeSmallParts,
} from './SDFPostProcessing';
import { SDF_ISO_THRESHOLD } from './sdfConstants';
import { maybeYield } from './utils';

/** Default lower HU bound for a planning CT (spec default, −300 HU). */
export const BODY_CONTOUR_CT_THRESHOLD_HU = -300;

/**
 * User-adjustable threshold range. The clinically meaningful body/air
 * boundary lives between air (−1000 HU) and lung parenchyma (≈ −700 to
 * −500 HU); bounding the control at −100 HU keeps soft tissue always
 * inside the outline. The image's own min/max (previously the slider
 * bounds) let the control wander into ranges where the "outline" would
 * exclude organs.
 */
export const BODY_CONTOUR_THRESHOLD_MIN_HU = -1000;
export const BODY_CONTOUR_THRESHOLD_MAX_HU = -100;

/**
 * Default keep-N for the per-slice (2D) keep-largest stage. N = 3
 * tolerates limbs that appear as separate cross-sections beside the trunk
 * on some slices (two arms + trunk) while still stripping smaller
 * in-plane debris such as straps and cables before the 3D pick.
 */
export const BODY_CONTOUR_DEFAULT_KEEP_2D_COUNT = 3;

/** Default keep-N for the 3D keep-largest stage: the body is one
 *  component, so N = 1 removes the treatment couch in the default case. */
export const BODY_CONTOUR_DEFAULT_KEEP_3D_COUNT = 1;

/**
 * Default floor for the optional min-size stage, expressed as an
 * IN-PLANE AREA judged per slice — a per-slice VOLUME test would delete
 * thin-slice anatomy wholesale. 500 mm² (the panel shows it as 5 cm²)
 * removes straps/cables/noise while every real body cross-section
 * (smallest ~2000 mm² at the skull apex) clears it. Domain unit: mm²,
 * like every other quantity crossing into the engine — the cm² the user
 * sees is a UI-boundary conversion (see docs/ui_specs.md).
 */
export const BODY_CONTOUR_MIN_PART_AREA_MM2 = 500;

export interface BodyContourMaskParams {
    /** Untransformed grid the `floatPixelData` volume is indexed by. */
    imageGrid: Grid;
    /** Row-major intensity volume aligned with `imageGrid`, in the
     *  volume's own quantified unit. */
    floatPixelData: ArrayLike<number>;
    /** Inclusive lower bound in the volume's unit; every voxel ≥ this
     *  is a candidate. */
    thresholdHu: number;
    /**
     * Inclusive UPPER bound, so a voxel is a candidate iff
     * `thresholdHu ≤ value ≤ upperThresholdHu`. Omitted = unbounded
     * (+Infinity), which is exactly the historical single-sided
     * behavior — the Body preset's, whose only clinical bound is the
     * lower (air) one. The parameter exists because the Bone preset
     * bounds its band: at 150 HU with no ceiling a "Bones" structure
     * also captures metal implants, fillings, clips and contrast, which
     * the band excludes. Non-finite samples classify out of range
     * inside {@link sampleIntensityMaskSlice}, so a NaN bound excludes
     * everything rather than silently widening the band.
     */
    upperThresholdHu?: number;
    /**
     * Per-slice (2D) cavity fill; defaults to true. The Body preset does
     * not expose the flag — an outline with unfilled lung/bowel air is
     * never a valid EXTERNAL. The Bone preset exposes it default-OFF for
     * a clinical reason: a 2D fill would swallow the spinal canal, which
     * the vertebral ring encloses in-plane on most slices.
     */
    fillCavities2d?: boolean;
    /**
     * Optional world-space (mm) axis-aligned restriction. Only voxels
     * inside the box (snapped outward to whole voxels, see
     * {@link gridSubRange}) are threshold candidates — everything
     * outside is background BEFORE any cleanup stage runs, and the CPU
     * stages iterate only the snapped window subvolume. A full-volume
     * box is normalized to "no restriction" (byte-identical to omitting
     * the field); a box entirely outside the volume yields an
     * all-background mask flagged by
     * {@link BodyContourMaskResult.isEmpty}.
     */
    boundsMm?: WorldBoundsMm;
    /**
     * Body the restriction occupies within {@link boundsMm}; defaults to
     * `'box'`, the whole window. `'ellipsoid'` additionally excludes every
     * voxel outside the ellipsoid inscribed in the snapped window (a
     * sphere while the box is a cube), which is what keeps a neighbouring
     * hot structure sitting in a box CORNER out of a lesion-scaled region.
     * The window is unchanged either way, so a restricted run still costs
     * O(window); only the classification narrows. Ignored without
     * `boundsMm` — there is no ellipsoid without bounds to inscribe it in.
     */
    regionShape?: VoiShape;
    signal?: AbortSignal;
}

export interface BodyContourMaskResult {
    /** Binary per-slice masks (255 body / sub-threshold background). */
    sliceMasks: Uint8Array[];
    /**
     * True when no voxel of the final mask is classified body — the box
     * was off the volume / in air, or the threshold excluded everything.
     * An explicit fact so the UI can refuse to present an empty outline
     * as a valid result. The cleanup stages (min-size, keep-largest)
     * run later, on the GPU-processed field (see
     * {@link bodyContourTextureFromMask}) — no labeling runs in the
     * mask phase.
     */
    isEmpty: boolean;
    /**
     * The snapped restriction window the mask was built under, or null
     * when unrestricted (no box, or a full-volume box). Consumed by
     * {@link bodyContourTextureFromMask} for the final cut — see
     * {@link cutFieldToRegion} for why that cut exists.
     */
    window: VoxelSubRange | null;
    /**
     * Shape the window was classified under, carried so the final cut
     * reproduces the mask's own selection. A cut back to the plain
     * window would let smoothing and margin leave the ellipsoid standing
     * in the corners the mask excluded.
     */
    windowShape: VoiShape;
    /**
     * The region's EXACT fractional bounds in image-grid voxel space, or
     * null when unrestricted. The final cut places the surface here
     * rather than at {@link window}, which is snapped outward and so
     * sits up to a voxel per face beyond what the user drew.
     */
    windowBounds: VoxelBounds | null;
}

/**
 * CPU stages of the extraction: threshold → [2D cavity fill]. Pure
 * (typed-array math, no GL), abortable, deterministic — the
 * unit-testable core, and the phase the mask cache retains across every
 * later parameter change (the cleanup stages all run post-smoothing on
 * the GPU field). A restriction window runs the stages on the window
 * subvolume and re-embeds into full-size planes — byte-identical to
 * thresholding whole slices and clearing outside the window (verified
 * by a golden test).
 */
export async function extractBodyContourMask(
    params: BodyContourMaskParams
): Promise<BodyContourMaskResult> {
    const { imageGrid, floatPixelData, boundsMm } = params;
    const nx = imageGrid.sizeX;
    const ny = imageGrid.sizeY;
    const nz = imageGrid.sizeZ;
    // No bounds, no ellipsoid to inscribe: the shape is only meaningful
    // relative to a window.
    const shape: VoiShape =
        boundsMm !== undefined ? (params.regionShape ?? 'box') : 'box';

    // Resolve the optional restriction to a voxel window once. A
    // full-volume BOX is normalized away here so a restricted run with
    // the default box is byte-identical to an unrestricted one (they
    // also share a cache key upstream, see bodyContourMaskKey). A
    // full-volume ELLIPSOID still excludes the volume's corners, so it
    // is a restriction and keeps its window.
    const subRange =
        boundsMm !== undefined ? gridSubRange(imageGrid, boundsMm) : null;
    const exactBounds =
        boundsMm !== undefined ? gridVoxelBounds(imageGrid, boundsMm) : null;
    const window =
        subRange !== null &&
        (shape === 'ellipsoid' || !subRangeIsFullGrid(imageGrid, subRange))
            ? subRange
            : null;

    // A box wholly outside the volume clamps to an empty window: the
    // documented all-background result, produced directly (no stage
    // runs) and flagged isEmpty so the UI guards can refuse it.
    if (window !== null && subRangeIsEmpty(window)) {
        const empty: Uint8Array[] = new Array(nz);
        for (let z = 0; z < nz; z++) {
            empty[z] = new Uint8Array(nx * ny);
        }
        return {
            sliceMasks: empty,
            isEmpty: true,
            window,
            windowShape: shape,
            windowBounds: exactBounds,
        };
    }

    if (window === null) {
        return runCpuMaskStages(imageGrid, floatPixelData, params, 'box');
    }

    // Restricted run: crop the HU subvolume, run the identical stages on
    // the cropped grid, then re-embed into zeroed full-size planes. The
    // crop's own index space IS the window, so the ellipsoid inscribed in
    // it needs no world-space transform.
    const cropped = cropHuSubvolume(imageGrid, floatPixelData, window);
    const sub = await runCpuMaskStages(
        cropped.grid,
        cropped.huData,
        params,
        shape
    );
    return {
        sliceMasks: embedWindowMasks(sub.sliceMasks, nx, ny, nz, window),
        isEmpty: sub.isEmpty,
        window,
        windowShape: shape,
        windowBounds: exactBounds,
    };
}

/**
 * The threshold → [2D cavity fill] sequence over one grid + intensity
 * volume —
 * either the full image or the restriction-window crop, which is what
 * makes the restricted run cost O(window) instead of O(volume).
 */
async function runCpuMaskStages(
    grid: Grid,
    floatPixelData: ArrayLike<number>,
    stages: Pick<
        BodyContourMaskParams,
        'thresholdHu' | 'upperThresholdHu' | 'fillCavities2d' | 'signal'
    >,
    shape: VoiShape
): Promise<BodyContourMaskResult> {
    const { thresholdHu, signal } = stages;
    const upperThresholdHu =
        stages.upperThresholdHu ?? Number.POSITIVE_INFINITY;
    const fillCavitiesEnabled = stages.fillCavities2d ?? true;
    const nx = grid.sizeX;
    const ny = grid.sizeY;
    const nz = grid.sizeZ;

    // Threshold every slice against the INCLUSIVE band
    // [thresholdHu, upperThresholdHu]. An omitted upper bound resolves to
    // +Infinity, the single-sided classification the Body preset wants:
    // metal and contrast are body, only the lower (air) bound is
    // clinical. A preset that bounds its band (Bone) supplies the ceiling
    // so densities above its material — implants, fillings, clips,
    // contrast — fall outside the structure. Non-finite samples classify
    // out of range inside sampleIntensityMaskSlice.
    let lastYieldTick = performance.now();
    const masks: Uint8Array[] = new Array(nz);
    // The grid IS the window here (an unrestricted run passes the image
    // grid, a restricted one the crop), so the ellipsoid inscribes in it.
    const gridRange: VoxelSubRange = {
        minX: 0,
        minY: 0,
        minZ: 0,
        maxX: nx,
        maxY: ny,
        maxZ: nz,
    };
    for (let z = 0; z < nz; z++) {
        const slice = sampleIntensityMaskSlice(
            grid,
            grid,
            floatPixelData,
            z,
            thresholdHu,
            upperThresholdHu
        );
        if (shape === 'ellipsoid') {
            clearSliceOutsideEllipsoid(slice, nx, ny, gridRange, z);
        }
        masks[z] = slice;
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }

    // The min-size stage runs POST-smoothing on the metric field (see
    // applyFieldCleanupStages) — the mask phase is threshold + fill only.
    // The ellipsoid clip precedes the fill for the same reason the box
    // crop does: a stage runs on what the region already selected, never
    // on what it excluded.
    const filled = fillCavitiesEnabled
        ? (await fillCavities2D(masks, nx, ny, nz, SDF_ISO_THRESHOLD, signal))
              .sdfU8
        : masks;

    return {
        sliceMasks: filled,
        // Center-out scan: the body is central when present, so the
        // non-empty (normal) case exits almost immediately.
        isEmpty: !SignedDistanceField.hasPixelsAboveThreshold(
            filled,
            nz,
            SDF_ISO_THRESHOLD
        ),
        window: null,
        windowShape: shape,
        windowBounds: null,
    };
}

/**
 * Skin, in voxels, the MASK phase leaves around the ellipsoid.
 *
 * The mask is binary, so the boundary it can express falls on voxel
 * faces. The surface is placed later and metrically, by
 * {@link intersectFieldWithInscribedEllipsoid}, and that cut can only
 * land between two voxels if the field carries real values on BOTH sides
 * of the true surface — cut to the exact ellipsoid here and everything
 * outside is already zero, leaving the metric pass nothing to
 * interpolate against and the staircase standing. Two voxels is
 * comfortably more than the one the interpolation reaches across, and
 * narrow enough that the cleanup stages still judge the region's own
 * content rather than the whole box.
 */
const MASK_ELLIPSOID_SLACK_VOXELS = 2;

/**
 * Zero every voxel of one mask slice lying outside the ellipsoid
 * inscribed in `range`, plus its {@link MASK_ELLIPSOID_SLACK_VOXELS}
 * skin, in place. This bounds what the cleanup stages see; it does not
 * place the surface.
 */
function clearSliceOutsideEllipsoid(
    slice: Uint8Array,
    nx: number,
    ny: number,
    bounds: VoxelBounds,
    z: number
): void {
    for (let y = 0; y < ny; y++) {
        const row = y * nx;
        const { lo, hi } = regionCenterXSpan(
            bounds,
            'ellipsoid',
            y,
            z,
            MASK_ELLIPSOID_SLACK_VOXELS
        );
        slice.fill(0, row, row + Math.max(0, Math.min(nx, lo)));
        slice.fill(0, row + Math.max(0, Math.min(nx, hi)), row + nx);
    }
}

/**
 * Copy the restriction window's HU voxels into a dense subvolume with a
 * matching sub-grid.
 *
 * The window is the half-open voxel range `[min, max)` already snapped
 * outward from the user's world box (see {@link gridSubRange}), so every
 * voxel it names exists in `imageGrid` and no partially covered voxel is
 * dropped. Each row of the window is contiguous in the source, so the
 * copy walks one destination-contiguous run per (z, y).
 *
 * The sub-grid keeps the image grid's rotation and resolution and moves
 * only the translation, to the world position of the window's `min`
 * corner — that keeps `sampleIntensityMaskSlice` on its identity fast
 * path.
 */
function cropHuSubvolume(
    imageGrid: Grid,
    floatPixelData: ArrayLike<number>,
    window: VoxelSubRange
): { grid: Grid; huData: Float32Array } {
    const cnx = window.maxX - window.minX;
    const cny = window.maxY - window.minY;
    const cnz = window.maxZ - window.minZ;
    const huData = new Float32Array(cnx * cny * cnz);
    const plane = imageGrid.sizeX * imageGrid.sizeY;
    let dst = 0;
    for (let z = window.minZ; z < window.maxZ; z++) {
        const zBase = z * plane;
        for (let y = window.minY; y < window.maxY; y++) {
            let src = zBase + y * imageGrid.sizeX + window.minX;
            for (let x = 0; x < cnx; x++) {
                huData[dst++] = floatPixelData[src++];
            }
        }
    }

    const transformation = mat4.clone(imageGrid.getTransformation() as mat4);
    const origin = imageGrid.toWorldSpace(
        vec3.fromValues(window.minX, window.minY, window.minZ)
    );
    transformation[12] = origin[0];
    transformation[13] = origin[1];
    transformation[14] = origin[2];
    return {
        grid: new Grid(
            cnx,
            cny,
            cnz,
            imageGrid.resX,
            imageGrid.resY,
            imageGrid.resZ,
            transformation
        ),
        huData,
    };
}

/**
 * Re-embed the window subvolume's slice masks into zeroed full-size
 * planes at the window offset. Slices outside the z-window are plain
 * zero planes — pure background, exactly what thresholding them under
 * the restriction would have produced.
 */
function embedWindowMasks(
    windowMasks: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    window: VoxelSubRange
): Uint8Array[] {
    const cnx = window.maxX - window.minX;
    const out: Uint8Array[] = new Array(nz);
    for (let z = 0; z < nz; z++) {
        const slice = new Uint8Array(nx * ny);
        out[z] = slice;
        if (z < window.minZ || z >= window.maxZ) {
            continue;
        }
        const sub = windowMasks[z - window.minZ];
        for (let y = window.minY; y < window.maxY; y++) {
            const subRow = (y - window.minY) * cnx;
            slice.set(sub.subarray(subRow, subRow + cnx), y * nx + window.minX);
        }
    }
    return out;
}

/** CPU cleanup stages applied to the post-smoothing field: a disabled
 *  stage is simply omitted (final fill: false/omitted = off). */
export interface FieldCleanupStages {
    /**
     * In-plane area floor in mm² (domain unit), applied per slice to 2D
     * components of the smoothed field — an area judged per slice, never
     * a volume. The panel displays and accepts cm² and converts at the
     * UI boundary; nothing below this line knows about cm.
     */
    minPartAreaMm2?: number;
    /** Keep only the N largest components PER SLICE (integer ≥ 1). */
    keepLargest2dCount?: number;
    /** Keep only the N largest 3D components (integer ≥ 1). */
    keepLargest3dCount?: number;
    /**
     * Re-fill per-slice cavities AFTER the cleanup stages (Body preset):
     * smoothing can pull a mask-phase fill back below the iso threshold
     * and the keep/min-size stages can carve interior islets, so without
     * this the applied EXTERNAL showed reopened holes. Bone locks it off
     * — a re-fill would swallow the spinal canal.
     */
    finalFillHoles2d?: boolean;
}

/** Voxel dimensions (mm) of the grid the field is sampled on — needed by
 *  the min-size stage's area → component-volume conversion. */
export interface VoxelSizeMm {
    resXMm: number;
    resYMm: number;
    resZMm: number;
}

/**
 * Cleanup stages of the pipeline, applied to the FINAL field — the
 * metric SDF produced by the GPU reinitialization and optional smoothing
 * — in fixed order: minimum in-plane area → keep-largest per slice →
 * keep-largest 3D → final 2D cavity re-fill. They run after smoothing
 * because smoothing can merge, split or shrink marginal components: only
 * a selection made on the smoothed field judges the components the user
 * actually sees. Unlike the hard binary CPU mask phase, the field here
 * has a metric falloff, so the SDF skip optimization stays ENABLED — the
 * same trust every other operation's post-processing places in it.
 *
 * Returns the (possibly replaced) slice arrays and whether any voxel
 * changed; `changed === false` means the content is identical to the
 * input, so the caller can skip re-uploading the GPU texture. Exported
 * separately from {@link bodyContourTextureFromMask} so the stage
 * composition is unit-testable without a GL context. Throws AbortError
 * when `signal` fires.
 */
export async function applyFieldCleanupStages(
    field: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    stages: FieldCleanupStages,
    voxelSizeMm: VoxelSizeMm,
    signal?: AbortSignal
): Promise<{ field: Uint8Array[]; changed: boolean }> {
    const options2d: ConnectedComponentOptions = { connectivity: '2d' };
    let current = field;
    let changed = false;
    if (stages.minPartAreaMm2 !== undefined) {
        // The min-size floor is an in-plane area judged per slice, which
        // is exactly the shared primitive's '2D' contract: component voxel
        // count × resX × resY ≥ minComponentSizeMm (mm²), slice thickness
        // excluded. No unit adaptation happens here — both sides speak
        // domain mm.
        //
        // allowEmptyResult: this pipeline needs the STRICT floor (no
        // never-empty keep-largest). A sole component below the enabled
        // minimum must not be delivered as valid; the preview and apply
        // paths carry the empty-result refusal themselves
        // (`thresholdSeg.emptyResult`).
        const cleaned = await removeSmallParts(
            current,
            nx,
            ny,
            nz,
            SDF_ISO_THRESHOLD,
            voxelSizeMm.resXMm,
            voxelSizeMm.resYMm,
            voxelSizeMm.resZMm,
            stages.minPartAreaMm2,
            '2D',
            signal,
            { allowEmptyResult: true }
        );
        current = cleaned.sdfU8;
        changed = changed || cleaned.numComponentsRemoved > 0;
    }
    if (stages.keepLargest2dCount !== undefined) {
        const kept = await keepLargestComponents(
            current,
            nx,
            ny,
            nz,
            SDF_ISO_THRESHOLD,
            stages.keepLargest2dCount,
            signal,
            options2d
        );
        current = kept.sdfU8;
        changed = changed || kept.numComponentsRemoved > 0;
    }
    if (stages.keepLargest3dCount !== undefined) {
        const kept = await keepLargestComponents(
            current,
            nx,
            ny,
            nz,
            SDF_ISO_THRESHOLD,
            stages.keepLargest3dCount,
            signal,
            { connectivity: '3d' }
        );
        current = kept.sdfU8;
        changed = changed || kept.numComponentsRemoved > 0;
    }
    if (stages.finalFillHoles2d === true) {
        const filled = await fillCavities2D(
            current,
            nx,
            ny,
            nz,
            SDF_ISO_THRESHOLD,
            signal
        );
        // fillCavities2D always allocates, so identity is no signal. Its
        // reported count is exact: the fill writes 255 only over voxels
        // below the threshold, so `filledVoxelCount === 0` means the
        // output is byte-identical to the input and the GPU texture need
        // not be re-uploaded.
        changed = changed || filled.filledVoxelCount > 0;
        current = filled.sdfU8;
    }
    return { field: current, changed };
}

/** GPU-phase parameters: a disabled stage is simply omitted. */
export interface BodyContourTextureStages extends FieldCleanupStages {
    /**
     * Gaussian sigma of the smoothing stage; omitted = smoothing
     * disabled. The PHYSICAL parameter, not a level index: the panel's
     * strength control is continuous, so it resolves its position
     * through `getSigmaForStrength` and hands the sigma down — the same
     * value the generation description records.
     */
    smoothingSigma?: number;
    /**
     * Symmetric outward margin in mm (High density preset), applied
     * after the cleanup stages and before the keep-inside intersection
     * and restriction-window clamp. Omitted or ≤ 0 = no margin stage.
     */
    marginMm?: number;
    /**
     * Intersect the result with this texture (keep-inside-EXTERNAL,
     * Bone / High density): applied AFTER the margin — the margin must
     * not regrow past the clip — and before the restriction-window
     * clamp. Read-only here; the CALLER owns the texture's lifetime.
     */
    keepInsideTexture?: ContourTexture;
}

/**
 * Clamp a processed texture back to the restriction window (given in
 * IMAGE-grid voxel indices). The texture grid is axis-aligned to the
 * image grid at an integer voxel offset (every producer in this pipeline
 * crops against the image grid), so the window translates by that offset.
 * Re-uploads only when a voxel actually changed.
 */
function clampTextureToWindow(
    texture: ContourTexture,
    imageGrid: Grid,
    bounds: VoxelBounds,
    shape: VoiShape
): void {
    const textureGrid = texture.textureGrid;
    const origin = imageGrid.toVoxelSpace(textureGrid.getPosition());
    const ox = Math.round(origin[0]);
    const oy = Math.round(origin[1]);
    const oz = Math.round(origin[2]);
    const pixels = texture.signedDistanceField.pixels;
    const changedSlices = cutFieldToRegion(
        pixels,
        textureGrid.sizeX,
        textureGrid.sizeY,
        textureGrid.sizeZ,
        {
            minX: bounds.minX - ox,
            minY: bounds.minY - oy,
            minZ: bounds.minZ - oz,
            maxX: bounds.maxX - ox,
            maxY: bounds.maxY - oy,
            maxZ: bounds.maxZ - oz,
        },
        shape,
        [textureGrid.resX, textureGrid.resY, textureGrid.resZ],
        ContourTexture.SDF_THRESHOLD / texture.margin
    );
    // Upload only what the clamp rewrote — the window complement, the
    // smoothing/margin bleed at its faces, and under an ellipsoid the
    // band the metric cut reshaped. A resetSdfData here
    // would be one more full-volume synchronous transfer per preview
    // (see reinitializeSDFWebGLToTexture) to fix a few slices.
    // arrayToTextureSlice keeps the CPU cache in lockstep: pixels[z]
    // already IS the cached array.
    const sdf = texture.signedDistanceField;
    for (const z of changedSlices) {
        sdf.arrayToTextureSlice(pixels[z], z);
    }
    if (changedSlices.length > 0) {
        // The keep-inside stage's content-crop reads (and caches)
        // isEmpty before this clamp runs, so a clamp that zeroed voxels
        // must drop that answer — narrowly, keeping the warm pixel
        // cache — or an emptied result could still claim non-empty to
        // the hooks' empty-result guards.
        texture.invalidateEmptyCache();
    }
}

/**
 * GPU stages and final selection: hand the CPU mask to the GPU on a
 * texture spanning exactly the image grid — so every generated contour
 * lies on an image slice and export can always resolve a SOP instance —
 * reinitialize to a metric SDF, Gaussian-smooth when a sigma is given,
 * run the cleanup stages on the resulting field (see
 * {@link applyFieldCleanupStages} for why they come after smoothing),
 * apply the optional symmetric margin, intersect with the optional
 * keep-inside texture, and cut a restricted run back to its region
 * (see {@link cutFieldToRegion}). Without smoothing the
 * reinitialization still runs: downstream consumers expect a metric
 * field.
 *
 * Splitting this from {@link extractBodyContourMask} lets callers cache
 * the mask across every later parameter change (smoothing, min-size,
 * keep-largest, fill re-apply, margin, keep-inside), so those tweaks
 * cost only this phase — never the multi-second CPU threshold/fill
 * re-run.
 *
 * Returns the processed {@link ContourTexture}, or undefined when the GPU
 * stage cannot run (GL contention or a failed allocation) — never a
 * partial result. The component statistics stay on the
 * {@link BodyContourMaskResult} the caller already holds. Throws
 * AbortError when `signal` fires during an abortable stage; the
 * intermediate texture is released on every exceptional path.
 */
export async function bodyContourTextureFromMask(
    imageGrid: Grid,
    mask: BodyContourMaskResult,
    stages: BodyContourTextureStages,
    signal?: AbortSignal
): Promise<ContourTexture | undefined> {
    const maskTexture = new ContourTexture(
        imageGrid,
        vec3.fromValues(imageGrid.sizeX, imageGrid.sizeY, imageGrid.sizeZ),
        imageGrid.getPosition()
    );
    try {
        // maskTexture is a CPU-pixels + grid carrier only — nothing ever
        // samples its GPU texture, so the mask is never uploaded into it
        // (a previous resetSdfData here was ~75 MB of dead texSubImage3D
        // per preview). Both consumers below read `pixels` (served from
        // the cache set here, no readback) plus the grid, and render into
        // a write target they allocate themselves. The constructor still
        // allocates the texture STORAGE (~50 MB at 512x512x200), held
        // until the delete() below: removing it would mean deferred
        // allocation in SignedDistanceField, whose failure mode — a
        // sampler reading an unallocated texture as black — is silent
        // rather than loud, so the transient allocation is the safer
        // trade on a clinical path.
        //
        // The cache arrays are NOT a private readback buffer: they alias
        // the BodyContourMaskCache entry that outlives this call. Safe
        // only because every consumer before delete() is read-only —
        // maskTexture must never be written through the slice mutators,
        // or the cached mask (and every later preview served from it)
        // would be silently corrupted.
        maskTexture.signedDistanceField?.setCachedPixels(mask.sliceMasks);
        let processed =
            stages.smoothingSigma === undefined
                ? await SDFOperations.reinitializeSDFWebGL(maskTexture)
                : await applyGaussianSmoothingSigmaGL(
                      maskTexture,
                      stages.smoothingSigma,
                      { signal }
                  );
        if (!processed) {
            return undefined;
        }
        try {
            if (
                stages.minPartAreaMm2 !== undefined ||
                stages.keepLargest2dCount !== undefined ||
                stages.keepLargest3dCount !== undefined ||
                stages.finalFillHoles2d === true
            ) {
                // Both GPU paths warm the result's pixel cache, so this
                // reads CPU arrays — no readback. resetSdfData keeps the
                // cache in lockstep with the upload.
                const cleaned = await applyFieldCleanupStages(
                    processed.signedDistanceField.pixels,
                    imageGrid.sizeX,
                    imageGrid.sizeY,
                    imageGrid.sizeZ,
                    {
                        minPartAreaMm2: stages.minPartAreaMm2,
                        keepLargest2dCount: stages.keepLargest2dCount,
                        keepLargest3dCount: stages.keepLargest3dCount,
                        finalFillHoles2d: stages.finalFillHoles2d,
                    },
                    {
                        resXMm: imageGrid.resX,
                        resYMm: imageGrid.resY,
                        resZMm: imageGrid.resZ,
                    },
                    signal
                );
                if (cleaned.changed) {
                    processed.resetSdfData(cleaned.field);
                }
            }
            if (stages.marginMm !== undefined && stages.marginMm > 0) {
                // Margin AFTER the keep stages (growing debris before
                // selecting components would change what survives) and
                // BEFORE the window clamp, so the margin can never carry
                // the result past the user's box.
                //
                // Crop to content first: applyMarginBase expands the grid,
                // reads the SDF back, shifts and de-falloffs it on the CPU,
                // then crops again — ~4 full-volume readbacks and 3 CPU
                // passes if it runs on the image grid (~75 MB a piece). The
                // margin toolbar never pays that because ROI textures are
                // already content-cropped; High density content is a few cm³
                // of implants in an otherwise empty volume. The crop is
                // voxel-aligned and reads the warm pixel cache, so it keeps
                // the integer-offset invariant clampTextureToWindow relies
                // on and costs one CPU scan, no readback.
                processed.cropToContentBounds();
                const grown = await SDFOperations.applySymmetricMargin(
                    processed,
                    stages.marginMm
                );
                processed.delete();
                processed = grown;
                if (signal?.aborted) {
                    throw new DOMException('Operation aborted', 'AbortError');
                }
            }
            if (stages.keepInsideTexture !== undefined) {
                // Keep-inside-EXTERNAL: intersect AFTER the margin (the
                // margin must never regrow past the clip) and before the
                // window clamp. In-place GPU AND through the shared
                // boolean primitive; content-crop first (cheap CPU scan
                // on the warm cache) so the boolean and the readback its
                // trailing content-crop performs run on the content
                // extent, not the image grid. Both crops are
                // voxel-aligned, preserving the integer-offset invariant
                // clampTextureToWindow relies on.
                processed.cropToContentBounds();
                SDFOperations.applyBoolean(
                    processed,
                    stages.keepInsideTexture,
                    BooleanOperation.AND,
                    processed
                );
                if (signal?.aborted) {
                    throw new DOMException('Operation aborted', 'AbortError');
                }
            }
            if (mask.windowBounds !== null) {
                // Final cut of a restricted run — see
                // {@link cutFieldToRegion}.
                clampTextureToWindow(
                    processed,
                    imageGrid,
                    mask.windowBounds,
                    mask.windowShape
                );
            }
            processed.cropToImageBounds();
            return processed;
        } catch (e) {
            // Release-with-acquisition: an abort (or any throw) in the
            // post stages must not strand the full-volume result.
            processed.delete();
            throw e;
        }
    } finally {
        maskTexture.delete();
    }
}

/**
 * What a previewed field contains, in DOMAIN units — the facts the panel
 * reports so the user can confirm that a preview whose outline is sparse
 * on screen (High density: scattered implant fragments) actually found
 * something. Never derived from an intermediate stage: see
 * {@link thresholdSegmentationTextureStats}.
 */
export interface ThresholdSegmentationStats {
    /**
     * Volume of the previewed structure in mm³: the number of voxels at
     * or above {@link SDF_ISO_THRESHOLD} — the same inside test every
     * other stage of this pipeline applies — times the voxel volume.
     * The UI converts to cm³ at its boundary.
     */
    volumeMm3: number;
    /**
     * Number of 3D connected components (face-connected, 6-neighbour) of
     * the same field. Present only when the caller asked for it: the
     * count answers a question only the High density preset's output
     * raises, and the labeling pass is not run for the presets that
     * would not display it.
     */
    componentCount?: number;
}

/** Count the voxels the pipeline classifies INSIDE (>= iso), the same
 *  test {@link SignedDistanceField.hasPixelsAboveThreshold} and every
 *  cleanup stage apply. */
async function countInsideVoxels(
    field: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    signal?: AbortSignal
): Promise<number> {
    const plane = nx * ny;
    let lastYieldTick = performance.now();
    let inside = 0;
    for (let z = 0; z < nz; z++) {
        const slice = field[z];
        for (let i = 0; i < plane; i++) {
            if (slice[i] >= SDF_ISO_THRESHOLD) {
                inside++;
            }
        }
        lastYieldTick = await maybeYield(lastYieldTick, signal);
    }
    return inside;
}

/**
 * Statistics of ONE field: volume always, component count only when
 * asked for. Pure (typed-array math, no GL), abortable and deterministic
 * — the numbers are a count and a sum over a fixed index order, so no
 * iteration order can move them.
 *
 * When the count is requested both numbers come out of a SINGLE
 * {@link labelConnectedComponents} pass — `componentSizes` already
 * carries the per-label voxel counts, so summing them is the same
 * volume the counting loop would produce, from the same visit.
 *
 * The labeling runs with the SDF skip optimization DISABLED. The skip
 * assumes a metric field and jumps three voxels ahead wherever the field
 * reads "far from the surface"; the fields this function is handed are
 * usually metric (the GPU reinitialization guarantees it) but not
 * always — the margin stage rewrites the falloff on the CPU, and a
 * displayed count that silently merged two fragments because the skip
 * stepped over the gap between them is a wrong number presented as
 * valid. Disabling the skip only ever visits MORE voxels, so it cannot
 * make the answer worse, and the volume pass visits every voxel anyway.
 */
export async function computeThresholdSegmentationStats(
    field: Uint8Array[],
    nx: number,
    ny: number,
    nz: number,
    voxelSizeMm: VoxelSizeMm,
    options: { countComponents: boolean },
    signal?: AbortSignal
): Promise<ThresholdSegmentationStats> {
    const voxelVolumeMm3 =
        voxelSizeMm.resXMm * voxelSizeMm.resYMm * voxelSizeMm.resZMm;
    if (!options.countComponents) {
        const inside = await countInsideVoxels(field, nx, ny, nz, signal);
        return { volumeMm3: inside * voxelVolumeMm3 };
    }
    const { numComponents, componentSizes } = await labelConnectedComponents(
        field,
        nx,
        ny,
        nz,
        SDF_ISO_THRESHOLD,
        true,
        signal,
        { connectivity: '3d', disableSdfSkip: true }
    );
    // Ascending label order, index 0 (background) skipped — see
    // labelConnectedComponents.
    let inside = 0;
    for (let label = 1; label <= numComponents; label++) {
        inside += componentSizes[label];
    }
    return {
        volumeMm3: inside * voxelVolumeMm3,
        componentCount: numComponents,
    };
}

/**
 * Statistics of the field a {@link ContourTexture} actually holds — the
 * form the preview hook uses.
 *
 * Call it on the texture {@link bodyContourTextureFromMask} RETURNED and
 * nothing else: that texture is the one the preview wrapper displays and
 * the apply path consumes, so reading its pixels here is by construction
 * the post-margin, post-keep-inside, post-window-clamp field the user is
 * looking at. Computing from any earlier stage would report a structure
 * the viewport never showed.
 *
 * Cost: the read is served from the texture's CPU pixel cache whenever
 * it is warm — which it is on every path whose last mutation was a CPU
 * stage (the cleanup stages, the restriction-window clamp) — and
 * otherwise costs one readback of the CONTENT-CROPPED texture, which the
 * GPU stages leave behind them. No full-volume readback is added: the
 * texture is cropped to its content before the margin and the
 * keep-inside intersection.
 */
export async function thresholdSegmentationTextureStats(
    texture: ContourTexture,
    options: { countComponents: boolean },
    signal?: AbortSignal
): Promise<ThresholdSegmentationStats> {
    const grid = texture.textureGrid;
    return computeThresholdSegmentationStats(
        texture.pixels,
        grid.sizeX,
        grid.sizeY,
        grid.sizeZ,
        { resXMm: grid.resX, resYMm: grid.resY, resZMm: grid.resZ },
        options,
        signal
    );
}
